import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Aanbeveling in het winkelwagenpaneel: toont één product tegelijk met pijltjes, een maatkeuze en een
 * 'Ajouter'-knop. Voegt toe via dezelfde events als het productformulier, zodat het paneel zichzelf bijwerkt.
 */
class CartUpsell extends HTMLElement {
  #index = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#show(0);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  get #cards() {
    return Array.from(this.querySelectorAll('[data-upsell-card]'));
  }

  /** @param {number} index */
  #show(index) {
    const cards = this.#cards;
    if (!cards.length) return;
    this.#index = (index + cards.length) % cards.length;
    cards.forEach((card, i) => {
      card.hidden = i !== this.#index;
    });
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-upsell-prev]')) return this.#show(this.#index - 1);
    if (target.closest('[data-upsell-next]')) return this.#show(this.#index + 1);

    const addButton = target.closest('[data-upsell-add]');
    if (addButton instanceof HTMLButtonElement) this.#add(addButton);
  };

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const card = button.closest('[data-upsell-card]');
    const select = card?.querySelector('select');
    const variantId = select?.value;
    if (!variantId || button.disabled) return;

    const label = button.textContent;
    button.disabled = true;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (sectionIds) formData.append('sections', sectionIds);

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const result = await response.json();
      if (result.status) throw new Error(result.description || result.message || 'Add to cart failed');

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-upsell',
          sourceId: 'cart-upsell',
          itemCount: 1,
          sections: result.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      button.disabled = false;
      button.textContent = label;
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
